import { createContext, FC } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormInput } from '@microservices/wiskey-react-components'
import { Box, Grid, Skeleton, Typography } from '@mui/material'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { EditConfigMenu } from '@components/ConfigEditMenu'
import { EditConfigScriptDialog } from '@components/EditConfigScriptDialog'
import { GeneralButtons } from '@components/GeneralButtons'
import { TextSettings } from '@components/TextSettings'

import { EditConfigProvider } from '@providers/EditConfigProvider'

import { ENTITY_CONFIG_TYPE, GENERATOR_INPUT_TYPE, REGEX } from '@constants'

import { useStyleSettingsCreateOrEdit } from './hooks/useStyleSettingsCreateOrEdit'
import { PageContextType } from './types'

export const PageContext = createContext<PageContextType>({} as PageContextType)

export const StyleSettingsCreateOrEdit: FC = () => {
  const { t } = useTranslation()

  const {
    state: { isDirtyTextSettings },
    data: {
      methods,
      id,
      isCreate,
      textSettingsRef,
      initialTextSettings,
      isLoadingStyleSettingById,
      isLoadingStyleSettings,
    },
    handlers: { handleCancel, handleSave, handleTextExternalDirty },
  } = useStyleSettingsCreateOrEdit()

  const {
    formState: { isDirty },
  } = methods

  const contextValue = {
    methods,
    id,
    isCreate,
  }

  const modelType = isCreate ? 'create' : 'edit'

  return (
    <PageContext.Provider value={contextValue}>
      <EditConfigProvider entityType={ENTITY_CONFIG_TYPE.STYLE_SETTINGS}>
        <PageContentLayout>
          <Box sx={{ width: '100%' }}>
            <EditConfigScriptDialog />
            <FormProvider {...methods}>
              <Box p={1} pb={0}>
                <Grid
                  container
                  alignItems={'center'}
                  justifyContent={'space-between'}
                  pb={2}
                  py={0.5}
                >
                  <Typography variant='h5'>{t(`styleSettings.title.${modelType}`)}</Typography>
                  {id && <EditConfigMenu configId={id} />}
                </Grid>
                <FormInput
                  disabled={!isCreate}
                  inputType={GENERATOR_INPUT_TYPE.INPUT}
                  label={t(`styleSettings.internalId.label`)}
                  loading={isLoadingStyleSettingById}
                  name='code'
                  placeholder={t(`styleSettings.internalId.placeholder`)}
                  replacePattern={REGEX.MODEL_CODE_REPLACE_PATTERN}
                  size='small'
                  sx={{ pt: 1 }}
                  rules={{
                    required: true,
                    validate: value => value.trim().length !== 0,
                  }}
                />
                <FormInput
                  inputType={GENERATOR_INPUT_TYPE.INPUT}
                  label={t(`styleSettings.title.styleSetting`)}
                  loading={isLoadingStyleSettingById}
                  name='title'
                  placeholder={t(`styleSettings.title.styleSetting`)}
                  size='small'
                  sx={{ pt: 1 }}
                  rules={{
                    required: true,
                  }}
                />
                {isLoadingStyleSettingById ? (
                  <Box>
                    <Skeleton height={60} />
                    <Skeleton height={60} />
                    <Skeleton height={60} />
                  </Box>
                ) : (
                  <TextSettings
                    ref={textSettingsRef}
                    borderSettings={true}
                    initialTextState={initialTextSettings}
                    isEdit={true}
                    labelPlacement='top'
                    labelText={t('parameters.visual.columnText')}
                    onSetExternalDirty={handleTextExternalDirty}
                  />
                )}

                <Grid container justifyContent='flex-end'>
                  <GeneralButtons
                    cancelText={t('styleSettings.cancelButton')}
                    isLoading={isLoadingStyleSettingById || isLoadingStyleSettings}
                    saveText={t('styleSettings.saveButton')}
                    saveDisabled={
                      !(isDirty || isDirtyTextSettings) ||
                      isLoadingStyleSettingById ||
                      isLoadingStyleSettings
                    }
                    onCancel={handleCancel}
                    onSave={handleSave}
                  />
                </Grid>
              </Box>
            </FormProvider>
          </Box>
        </PageContentLayout>
      </EditConfigProvider>
    </PageContext.Provider>
  )
}
